import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import FulfillmentTypeBadge from '@/components/orders/FulfillmentTypeBadge'
import OrderSourceBadge from '@/components/orders/OrderSourceBadge'
import { DataTableColumnHeader } from '@/components/tables/DataTableColumnHeader'
import { parseIndexExactIdSearch } from '@/components/tables/clientIndexTable'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
  IndexTableSearchBox,
} from '@/components/tables/IndexTable'
import { getTransactionDisplay } from '@/components/transactions/getTransactionDisplay'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type { DirectionalSortConfig } from '@/components/ui/sortable-column-header'
import { Typography } from '@/components/ui/typography'
import { type PathHelpers, type PathLink, usePathHelper } from '@/hooks/usePathHelper'
import {
  SALES_SUMMARY_TRANSACTION_SORT,
  type SalesSummaryReportSearch,
} from '@/lib/services/reports/reports.schema'
import type {
  SalesSummaryReportAppliedFilters,
  SalesSummaryReportCollectionRow,
  SalesSummaryReportMeta,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import type { ColumnDef } from '@tanstack/react-table'
import { CustomerMonogram } from './-avatars'
import { formatTransactionDate } from './-formatters'
import {
  MISSING_CUSTOMER_LABEL,
  getCustomerDisplayName,
  getCustomerToneKey,
  hasProvidedCustomer,
} from './-helpers'
import { SectionCardHeader } from './-section-card-header'
import { SalesSummaryTransactionAside } from './-transaction-aside'

type TransactionRow = SalesSummaryReportCollectionRow
type TransactionColumn = ColumnDef<TransactionRow>

const salesSummaryTransactionSortColumns = {
  organization: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.ORGANIZATION_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.ORGANIZATION_DESC,
  },
  transaction: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.TRANSACTION_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.TRANSACTION_DESC,
  },
  salesOrder: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.SALES_ORDER_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.SALES_ORDER_DESC,
  },
  customer: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.CUSTOMER_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.CUSTOMER_DESC,
  },
  source: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.SOURCE_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.SOURCE_DESC,
  },
  fulfillment: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.FULFILLMENT_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.FULFILLMENT_DESC,
  },
  payout: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.PAYOUT_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.PAYOUT_DESC,
  },
  total: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.TOTAL_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.TOTAL_DESC,
  },
  createdAt: {
    asc: SALES_SUMMARY_TRANSACTION_SORT.CREATED_ASC,
    desc: SALES_SUMMARY_TRANSACTION_SORT.CREATED_DESC,
    defaultDirection: 'desc',
  },
} satisfies Record<string, DirectionalSortConfig>

type TransactionsCardProps = {
  rows: TransactionRow[]
  /** The Global report's server pages; shop rows page in the browser without it. */
  meta?: SalesSummaryReportMeta
  orgId: number
  timezone: string
  isGlobalReport?: boolean
  filters?: Partial<SalesSummaryReportAppliedFilters>
  routeLink?: PathLink
  search?: SalesSummaryReportSearch
}

/** The Global report's table params to navigate to. */
type GlobalTableParams = {
  search?: string
  sort?: string
  page?: number
  page_size?: number
}

export function TransactionsCard({
  rows,
  meta,
  orgId,
  timezone,
  isGlobalReport = false,
  filters = {},
  routeLink,
  search,
}: TransactionsCardProps) {
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionRow | null>(null)
  const [transactionSearch, setTransactionSearch] = useState('')
  const path = usePathHelper()
  const navigate = useNavigate()
  const activeSearch = isGlobalReport ? (filters.search ?? '') : transactionSearch
  const filteredRows = isGlobalReport ? rows : filterTransactionRows(rows, transactionSearch)
  // Stable cell/header types preserve content nodes during data and local-state updates.
  const columns = useMemo(
    () =>
      getTransactionColumns({
        timezone,
        orgId,
        path,
        isGlobalReport,
        showOrganization: isGlobalReport,
        serverSortable: isGlobalReport,
      }),
    [timezone, orgId, path, isGlobalReport]
  )
  // Shop reports page on the client, so without server metadata every filtered row is one page.
  const resolvedMeta = meta ?? getIndexPaginationMeta(filteredRows, 1, filteredRows.length || 10)
  const transactionCountLabel = isGlobalReport
    ? `${resolvedMeta.total} ${resolvedMeta.total === 1 ? 'transaction' : 'transactions'}`
    : filteredRows.length === rows.length
      ? `${rows.length} ${rows.length === 1 ? 'transaction' : 'transactions'}`
      : `${filteredRows.length} of ${rows.length} transactions`
  const handleAsideOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedTransaction(null)
    }
  }
  const updateGlobalTableParams = (params: GlobalTableParams) => {
    if (!routeLink) return

    const nextSearch = Object.prototype.hasOwnProperty.call(params, 'search')
      ? params.search
      : filters.search
    const nextSort = Object.prototype.hasOwnProperty.call(params, 'sort')
      ? params.sort
      : filters.sort

    void navigate({
      ...routeLink,
      search: {
        start_date: filters.start_date,
        end_date: filters.end_date,
        date_mode: filters.date_mode ?? undefined,
        source: filters.source ?? undefined,
        fulfillment_type: filters.fulfillment_type ?? undefined,
        granularity: filters.granularity ?? undefined,
        search: nextSearch || undefined,
        sort: nextSort ?? undefined,
        page: params.page ?? filters.page ?? 1,
        page_size: params.page_size ?? filters.page_size ?? resolvedMeta.page_size,
      },
      replace: Boolean(
        search?.start_date || search?.search || search?.sort || search?.page || search?.page_size
      ),
    })
  }
  const handleSearchCommit = (value: string) => {
    if (!isGlobalReport) {
      setTransactionSearch(value)
      return
    }

    updateGlobalTableParams({
      search: value.trim() || undefined,
      page: 1,
    })
  }

  return (
    <>
      <Card className="min-w-0 overflow-hidden">
        <SectionCardHeader
          title="Transactions"
          description="Orders and refunds in this range"
          count={transactionCountLabel}
        />
        <CardContent className="min-w-0">
          <IndexDataTable
            mode={isGlobalReport ? 'server' : 'client'}
            pagination={!isGlobalReport}
            data={filteredRows}
            columns={columns}
            sort={isGlobalReport ? (filters.sort ?? undefined) : undefined}
            onSortChange={
              isGlobalReport
                ? (sort) =>
                    updateGlobalTableParams({
                      sort: sort ?? undefined,
                      page: 1,
                    })
                : undefined
            }
            getRowId={(row) => row.transaction_id}
            getRowPresentation={(row) => ({
              selected: selectedTransaction?.transaction_id === row.transaction_id,
            })}
            onRowClick={(row) => {
              setSelectedTransaction(row)
            }}
            maxPageSize={50}
            defaultPageSize={10}
            syncPaginationToUrl={false}
            toolbar={
              <div className="border-b bg-muted/10 px-3 py-3">
                <IndexTableSearchBox
                  value={activeSearch}
                  onCommit={handleSearchCommit}
                  countText={transactionCountLabel}
                  placeholder="Search transactions..."
                />
              </div>
            }
            emptyState={
              activeSearch.trim()
                ? 'No transactions match your search.'
                : 'No transactions in this range.'
            }
          />
          {isGlobalReport ? (
            <IndexTablePagination
              meta={resolvedMeta}
              pageSize={resolvedMeta.page_size}
              onPageChange={(page) => updateGlobalTableParams({ page })}
              onPageSizeChange={(pageSize) =>
                updateGlobalTableParams({ page: 1, page_size: pageSize })
              }
              summary={`${resolvedMeta.total} ${resolvedMeta.total === 1 ? 'transaction' : 'transactions'}`}
            />
          ) : null}
        </CardContent>
      </Card>

      <SalesSummaryTransactionAside
        open={Boolean(selectedTransaction)}
        onOpenChange={handleAsideOpenChange}
        transaction={selectedTransaction}
        orgId={selectedTransaction?.organization_id ?? orgId}
        timezone={timezone}
      />
    </>
  )
}

function filterTransactionRows(rows: TransactionRow[], searchValue: string) {
  const query = normalizeTransactionSearchValue(searchValue)
  const exactId = parseIndexExactIdSearch(searchValue)

  // `#123` names an order; a transaction id is text, so a pasted one matches below.
  if (exactId !== null) {
    return rows.filter((row) => row.sales_order_id === exactId)
  }

  if (!query) {
    return rows
  }

  return rows.filter((row) =>
    getTransactionSearchValues(row).some((value) => value.includes(query))
  )
}

function getTransactionSearchValues(row: TransactionRow) {
  const display = getTransactionDisplay(row)

  return [
    row.sales_order_id ? `#${row.sales_order_id}` : '',
    row.sales_order_id,
    row.transaction_id,
    row.customer_name,
    display.label,
    row.source,
    row.fulfillment_type,
    row.total_sales,
    row.cash_total,
    row.net_sales,
  ]
    .map(normalizeTransactionSearchValue)
    .filter(Boolean)
}

function normalizeTransactionSearchValue(value: string | number | null | undefined) {
  return String(value ?? '')
    .trim()
    .replace(/^#/, '')
    .toLowerCase()
}

function getTransactionColumns({
  timezone,
  orgId,
  path,
  isGlobalReport = false,
  showOrganization = false,
  serverSortable = false,
}: {
  timezone: string
  orgId: number
  path: PathHelpers
  isGlobalReport?: boolean
  showOrganization?: boolean
  serverSortable?: boolean
}): TransactionColumn[] {
  const columns: (TransactionColumn | null)[] = [
    showOrganization
      ? {
          id: 'organization_name',
          accessorKey: 'organization_name',
          header: ({ column }) => <DataTableColumnHeader column={column} title="Organization" />,
          cell: ({ row }) => (
            <span className="block truncate font-medium">
              {row.original.organization_name ?? 'Organization'}
            </span>
          ),
          meta: {
            label: 'Organization',
            kind: 'primary',
            headClassName: 'min-w-[220px] px-4',
            cellClassName: 'px-4 py-3',
            ...(serverSortable
              ? { sortable: salesSummaryTransactionSortColumns.organization }
              : {}),
          },
        }
      : null,
    {
      id: 'transaction_type',
      accessorKey: 'transaction_type',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Transaction" />,
      cell: ({ row }) => {
        const display = getTransactionDisplay(row.original)

        return <Badge variant={display.badgeVariant}>{display.label}</Badge>
      },
      meta: {
        label: 'Transaction',
        kind: 'badge',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.transaction } : {}),
      },
    },
    {
      id: 'sales_order_id',
      accessorKey: 'sales_order_id',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order" />,
      cell: ({ row }) =>
        row.original.sales_order_id ? (
          <Link
            {...path.salesOrders
              .show(row.original.sales_order_id)
              .using({ orgId: row.original.organization_id ?? orgId }).link}
            onClick={(event) => event.stopPropagation()}
            className="font-medium text-foreground transition-colors hover:text-interaction hover:underline"
          >
            #{row.original.sales_order_id}
          </Link>
        ) : (
          <Typography variant="supporting">—</Typography>
        ),
      meta: {
        label: 'Order',
        kind: 'id',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.salesOrder } : {}),
      },
    },
    {
      id: 'customer_name',
      accessorKey: 'customer_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => {
        const customerName = getCustomerDisplayName(row.original)
        const hasCustomer = hasProvidedCustomer(row.original)

        if (!hasCustomer) {
          return (
            <Typography variant="supporting" italic>
              {MISSING_CUSTOMER_LABEL}
            </Typography>
          )
        }

        const customerBadge = (
          <CustomerMonogram
            name={customerName}
            toneKey={getCustomerToneKey(row.original.purchaser_customer_id, customerName)}
            size="sm"
          />
        )

        if (!row.original.purchaser_customer_id) {
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              {customerBadge}
              <span className="truncate font-medium">{customerName}</span>
            </div>
          )
        }

        return (
          <div className="flex min-w-0 items-center gap-2.5">
            {customerBadge}
            <Link
              {...path.customers
                .show(row.original.purchaser_customer_id)
                .using({ orgId: row.original.organization_id ?? orgId }).link}
              onClick={(event) => event.stopPropagation()}
              className="truncate font-medium text-foreground transition-colors hover:text-interaction hover:underline"
            >
              {customerName}
            </Link>
          </div>
        )
      },
      meta: {
        label: 'Customer',
        kind: 'primary',
        headClassName: 'min-w-[220px] px-4',
        cellClassName: 'px-4 py-3',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.customer } : {}),
      },
    },
    {
      id: 'source',
      accessorKey: 'source',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order Source" />,
      cell: ({ row }) => <OrderSourceBadge source={row.original.source} size="sm" />,
      meta: {
        label: 'Order Source',
        kind: 'badge',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.source } : {}),
      },
    },
    {
      id: 'fulfillment_type',
      accessorKey: 'fulfillment_type',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Fulfillment" />,
      cell: ({ row }) => (
        <FulfillmentTypeBadge fulfillmentType={row.original.fulfillment_type} size="sm" />
      ),
      meta: {
        label: 'Fulfillment',
        kind: 'badge',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.fulfillment } : {}),
      },
    },
    {
      id: 'total_sales',
      accessorKey: isGlobalReport ? 'total_sales' : 'cash_total',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Total" />,
      cell: ({ getValue }) => formatCurrency(getValue()),
      meta: {
        label: 'Total',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.total } : {}),
      },
    },
    {
      id: 'net_sales',
      accessorKey: 'net_sales',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Payout" />,
      cell: ({ row }) => formatCurrency(row.original.net_sales),
      meta: {
        label: 'Payout',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.payout } : {}),
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => formatTransactionDate(row.original.created_at, timezone),
      meta: {
        label: 'Date',
        kind: 'date',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium',
        ...(serverSortable ? { sortable: salesSummaryTransactionSortColumns.createdAt } : {}),
      },
    },
  ]

  return columns.filter((column) => column !== null)
}
